import { useEffect, useState } from "react";
import { X, Sparkles } from "lucide-react";
import { api } from "../lib/api";

// Post-login release ribbon.
//
// Shows a compact ribbon just under the top header on every authenticated page
// whenever the platform ships a new release the current browser has not seen
// yet. The user can expand the ribbon to read the release notes, or dismiss it
// with the X button.
//
// Dismissal is tracked client-side per browser via localStorage so no PHI or
// user preference is stored server-side for the ribbon. Every fresh device
// therefore sees the latest release once, which is the intended behavior for
// clinicians who rotate between shared workstations.

const STORAGE_KEY = "cocoa_seen_release_version";

export default function ReleaseRibbon() {
  const [release, setRelease] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/platform/releases/latest");
        const rel = r.data && r.data.release;
        if (!rel || !rel.version) return;
        if (cancelled) return;
        const seen = localStorage.getItem(STORAGE_KEY) || "";
        setRelease(rel);
        if (seen !== rel.version) setVisible(true);
      } catch {
        // Silent, the ribbon is a nice-to-have and never blocks the app.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const dismiss = () => {
    if (release && release.version) localStorage.setItem(STORAGE_KEY, release.version);
    setVisible(false);
    setExpanded(false);
  };

  if (!visible || !release) return null;

  return (
    <div data-testid="release-ribbon" data-release-version={release.version}
      className="mb-4 rounded-2xl border border-[#E6D9C4] bg-gradient-to-r from-[#FFF5E1] via-[#FFF3E0] to-[#FFE7BF] text-[#4E342E] shadow-[0_6px_18px_-10px_rgba(178,106,0,0.35)]">
      <div className="flex flex-wrap items-center gap-3 px-4 py-2.5 sm:px-5">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FFB300] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#3E2723]">
          <Sparkles size={11} /> New in {release.version}
        </span>
        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-[#3E2723]" title={release.title}>
          {release.title}
        </span>
        <button data-testid="release-ribbon-toggle" onClick={() => setExpanded((v) => !v)}
          className="rounded-full border border-[#B26A00]/40 bg-white/70 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-[#8D5A00] transition-colors hover:bg-white">
          {expanded ? "Hide details" : "See what changed"}
        </button>
        {release.cta_href && (
          <a data-testid="release-ribbon-cta" href={release.cta_href}
            className="rounded-full bg-[#4E342E] px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white hover:bg-[#3E2723]">
            {release.cta_label || "Open"}
          </a>
        )}
        <button data-testid="release-ribbon-dismiss" onClick={dismiss}
          aria-label="Dismiss release notes"
          className="rounded-full p-1 text-[#6D4C41] transition-colors hover:bg-white/70 hover:text-[#3E2723]">
          <X size={14} />
        </button>
      </div>
      {expanded && (
        <div data-testid="release-ribbon-details" className="px-4 pb-3 sm:px-5">
          <div className="rounded-xl border border-[#FFCC80] bg-white/90 px-4 py-3">
            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#8D5A00]">
              Released {(release.published_at || "").slice(0, 10)}{release.published_by ? ` by ${release.published_by}` : ""}
            </div>
            <ul className="space-y-1.5 text-[13px] leading-relaxed text-[#3E2723]">
              {(release.notes || []).map((note, i) => (
                <li key={i} className="flex gap-2">
                  <span aria-hidden className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#B26A00]" />
                  <span>{note}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
